// Routes A2UI protocol messages (v0.8 and v0.9) to reducer dispatch

import type { SurfaceAction } from '../state/surfaceStore.ts';
import type { A2UIMessage } from '../types/A2UIProtocol.types.tsx';

import { isV08Message, normalizeV08Components, normalizeV08DataContents } from './v08Normalizer.ts';

// ── v0.9 message processing ────────────────────────────────────────────

function processV09Message(message: A2UIMessage): SurfaceAction {
	if ('type' in message && message.type === 'createSurface') {
		return {
			type: 'CREATE_SURFACE',
			surfaceId: message.surfaceId,
			catalogId: message.catalogId,
			rootId: message.rootId,
			components: message.components,
			dataModel: message.dataModel,
		};
	}

	if ('updateComponents' in message) {
		return {
			type: 'UPDATE_COMPONENTS',
			components: message.updateComponents.components,
		};
	}

	if ('updateDataModel' in message) {
		const { path, value } = message.updateDataModel;
		if (!path) {
			return {
				type: 'UPDATE_DATA_MODEL',
				operations: [],
			};
		}

		return {
			type: 'UPDATE_DATA_MODEL',
			operations: [value === undefined ? { op: 'remove', path } : { op: 'replace', path, value }],
		};
	}

	if ('deleteSurface' in message) {
		return {
			type: 'DELETE_SURFACE',
		};
	}

	throw new Error('Unknown A2UI v0.9 message shape');
}

// ── v0.8 message processing ────────────────────────────────────────────

function processV08Message(message: A2UIMessage): SurfaceAction {
	const msg = message as Record<string, unknown>;

	// surfaceUpdate → UPDATE_COMPONENTS (with normalized components)
	if ('surfaceUpdate' in msg) {
		const surfaceUpdate = msg.surfaceUpdate as {
			components: Array<{ component: Record<string, Record<string, unknown>>; id: string }>;
			surfaceId?: string;
		};
		const normalizedComponents = normalizeV08Components(surfaceUpdate.components);
		return {
			type: 'UPDATE_COMPONENTS',
			components: normalizedComponents,
		};
	}

	// beginRendering → INIT_SURFACE (sets surface metadata, triggers render)
	if ('beginRendering' in msg) {
		const beginRendering = msg.beginRendering as {
			catalogId?: string;
			root: string;
			surfaceId?: string;
		};
		return {
			type: 'INIT_SURFACE',
			surfaceId: beginRendering.surfaceId ?? 'default',
			catalogId: beginRendering.catalogId,
			rootId: beginRendering.root,
		};
	}

	// dataModelUpdate → UPDATE_DATA_MODEL
	if ('dataModelUpdate' in msg) {
		const dataModelUpdate = msg.dataModelUpdate as {
			contents: unknown;
			path?: string;
			surfaceId?: string;
		};

		const normalizedData = normalizeV08DataContents(dataModelUpdate.contents as any);

		// v0.8 path may or may not start with '/'
		const basePath = dataModelUpdate.path
			? dataModelUpdate.path.startsWith('/')
				? dataModelUpdate.path
				: `/${dataModelUpdate.path}`
			: '';
		const isRootPath = basePath === '' || basePath === '/';

		if (isRootPath) {
			// Root replacement — set each top-level key
			const operations = Object.entries(normalizedData).map(([key, value]) => ({
				op: 'replace' as const,
				path: `/${key}`,
				value,
			}));
			return {
				type: 'UPDATE_DATA_MODEL',
				operations,
			};
		}

		return {
			type: 'UPDATE_DATA_MODEL',
			operations: [
				{
					op: 'replace',
					path: basePath,
					value: normalizedData,
				},
			],
		};
	}

	// deleteSurface — same format in both versions
	if ('deleteSurface' in msg) {
		return {
			type: 'DELETE_SURFACE',
		};
	}

	throw new Error('Unknown A2UI v0.8 message shape');
}

// ── Public API ──────────────────────────────────────────────────────────

export function processMessage(message: A2UIMessage): SurfaceAction {
	if (isV08Message(message)) {
		return processV08Message(message);
	}
	return processV09Message(message);
}

export function processMessages(messages: A2UIMessage[]): SurfaceAction[] {
	return messages.map(processMessage);
}
